/**
 * rulesVocabulary.ts — the ONE vocabulary for the Rules & Automations surface.
 *
 * The automations table, the detail drawer, and the create builder all speak
 * about the same rule engine, and they drifted apart precisely when each held
 * its own copy of the words (the table once printed `Cron 0 9 * * *` while the
 * wizard, for the very same rule, offered "Daily at 09:00"). Everything here
 * is presentation only: how a category, parameter, schedule, or severity is
 * SAID. What is stored and what runs never passes through this file.
 */
import type { Severity } from '@cwos/ui-shared';

export const SEV_PILL: Record<string, Severity> = {
  critical: 'urgent',
  warning: 'look',
  info: 'unsure',
};

const CATEGORY_LABEL: Record<string, string> = {
  missed_shift: 'Missed shift',
  incomplete_documentation: 'Incomplete documentation',
  care_gap: 'Care gap',
  geofence: 'Geofence',
  organization_structure: 'Organization setup',
};

/**
 * A trigger category, in words.
 *
 * `organization_structure` was missing from the map above and fell through to
 * the raw key, so the rules table and the create wizard both printed
 * "organization_structure" beside siblings reading "Geofence" and "Care gap".
 *
 * The fallback no longer prints a raw key either. A category this file has not
 * been taught about becomes "Organization setup"-shaped prose rather than a
 * database identifier: snake_case is split, and only the first word is
 * capitalised, so a future category reads like a label on the day it ships
 * rather than on the day someone remembers to add it here.
 */
export function categoryLabel(category: string | null | undefined): string {
  if (!category) return 'Rule';
  const known = CATEGORY_LABEL[category];
  if (known) return known;
  const words = category.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Rule';
}

export interface SchedulePreset {
  label: string;
  value: Record<string, unknown>;
}

/** The schedule presets cover every catalog default (interval + cron). */
export const SCHEDULE_PRESETS: SchedulePreset[] = [
  { label: 'Every 15 minutes', value: { kind: 'interval', intervalMs: 900_000 } },
  { label: 'Hourly', value: { kind: 'interval', intervalMs: 3_600_000 } },
  { label: 'Daily at 08:00', value: { kind: 'cron', expression: '0 8 * * *' } },
  { label: 'Daily at 09:00', value: { kind: 'cron', expression: '0 9 * * *' } },
  { label: 'Daily at 10:00', value: { kind: 'cron', expression: '0 10 * * *' } },
  { label: 'Daily at 20:00', value: { kind: 'cron', expression: '0 20 * * *' } },
  { label: 'Weekly, Monday 08:00', value: { kind: 'cron', expression: '0 8 * * 1' } },
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * A five-field cron expression in plain language, or null when this function
 * cannot say it plainly and honestly.
 *
 * Returning null matters more than the happy path. A wrong schedule shown
 * confidently is worse than an unfamiliar one shown as-is: somebody would
 * configure against it. Anything with a step, a list, a range or a
 * day-of-month restriction is left to the caller.
 */
export function describeCron(expression: string): string | null {
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [min, hour, dom, month, dow] = parts as [string, string, string, string, string];
  // Only plain integers and '*' are interpretable here.
  const plain = (v: string) => /^\d+$/.test(v);
  if (!plain(min) || !plain(hour)) return null;
  if (month !== '*' || dom !== '*') return null;

  const at = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
  if (dow === '*') return `Daily at ${at}`;
  if (plain(dow)) {
    const day = DAY_NAMES[Number(dow) % 7];
    return day ? `Weekly, ${day} ${at}` : null;
  }
  return null;
}

/**
 * How often an automation runs, for display.
 *
 * The rules table used to print `Cron 0 9 * * *` while the create wizard, for
 * the very same rule, offered "Daily at 09:00". Two vocabularies for one
 * setting, and the one shown in the table was the one nobody outside
 * engineering reads.
 *
 * The presets are consulted first so the table and the wizard agree by
 * construction; `describeCron` then covers expressions no preset matches. Raw
 * cron is the last resort and appears only when neither can say it plainly,
 * because an unreadable schedule is still better than a wrong one.
 */
export function describeSchedule(cfg: Record<string, unknown>): string {
  if (!cfg || typeof cfg !== 'object') return '—';
  const kind = cfg['kind'];
  if (kind === 'interval') {
    const ms = typeof cfg['intervalMs'] === 'number' ? cfg['intervalMs'] : 0;
    if (ms >= 3_600_000 && ms % 3_600_000 === 0) {
      const h = ms / 3_600_000;
      return h === 1 ? 'Hourly' : `Every ${h} hours`;
    }
    if (ms >= 60_000 && ms % 60_000 === 0) {
      const m = ms / 60_000;
      return m === 1 ? 'Every minute' : `Every ${m} minutes`;
    }
    return ms > 0 ? `Every ${ms}ms` : '—';
  }
  if (kind === 'cron') {
    const expr = typeof cfg['expression'] === 'string' ? cfg['expression'] : '';
    if (!expr) return '—';
    // The wizard's own wording wins, so both surfaces read identically.
    const preset = SCHEDULE_PRESETS.find(
      (p) => p.value['kind'] === 'cron' && p.value['expression'] === expr,
    );
    if (preset) return preset.label;
    return describeCron(expr) ?? expr;
  }
  return '—';
}

/** Options for the schedule select, always including the current value. */
export function scheduleOptions(
  current: Record<string, unknown>,
): { label: string; value: string }[] {
  const currentKey = JSON.stringify(current ?? {});
  const presets = SCHEDULE_PRESETS.map((p) => ({ label: p.label, value: JSON.stringify(p.value) }));
  if (current && Object.keys(current).length > 0 && !presets.some((p) => p.value === currentKey)) {
    presets.unshift({ label: `Current (${describeSchedule(current)})`, value: currentKey });
  }
  return presets;
}

/**
 * A rule parameter, in words.
 *
 * These keys come straight off the code-defined rule module's `defaultParams`,
 * so the configuration form was labelling its inputs `maxOpenHours` and
 * `graceMinutes` -- the identifiers a developer chose, printed at the person
 * being asked to set them. The unit is carried in the label because these are
 * all bare numbers on screen: "48" means nothing without "hours".
 *
 * The fallback splits camelCase rather than printing the key, so a parameter
 * added tomorrow reads as "min distance meters" instead of
 * "minDistanceMeters". Unknown-but-readable beats exact-and-opaque.
 */
const PARAM_LABEL: Record<string, string> = {
  // Keys the CATALOG offers when creating a rule.
  maxOpenHours: 'Hours a record may stay open',
  graceMinutes: 'Grace period (minutes)',
  gracePeriodMinutes: 'Grace period (minutes)',
  lookbackDays: 'Days to look back',
  lookaheadDays: 'Days to look ahead',
  minHoursPerWeek: 'Expected hours per week',
  minDistanceMeters: 'Minimum distance (metres)',
  deadlineDaysAfterPeriodEnd: 'Days after the period ends',
  minAgeHours: 'Hours an exception may wait for review',
  minConsecutiveDays: 'Consecutive working days',
  defaultEventDurationHours: 'Assumed visit length (hours)',
  programId: 'Limit to one program',
  siteId: 'Limit to one site',
  // Keys ALREADY STORED on live rules, which are not always the catalog's
  // defaults. An existing rule is edited through the same form, so covering
  // only the create-time keys would have left four of the five parameters in
  // this tenant still showing as identifiers.
  thresholdHours: 'Threshold (hours)',
  thresholdMinutes: 'Threshold (minutes)',
  graceHours: 'Grace period (hours)',
  tolerancePercent: 'Tolerance (%)',
};

export function paramLabel(key: string): string {
  const known = PARAM_LABEL[key];
  if (known) return known;
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key;
}

export function asNumber(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** Numeric parameters render as number inputs; everything else as text. */
export function paramKind(v: unknown): 'number' | 'text' {
  return typeof v === 'number' ? 'number' : 'text';
}
